import { useEffect, useState } from 'react'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import StyledTable from '@components/StyledTable'
import { ToggleSetButton } from '@components/ToggleSetButton'
import FilterKpi from '@components/toolbar/FilterKpi'
import type { Entity } from '@models/Entity'
import type { GroupedOption } from '@models/GroupedOption'
import { futureDay, pastDay } from '@util/date-util'

import type { Project } from '../../../aansluiting/project/types/Project'
import { useAuthenticatedUser } from '../../../auth/hooks/useAuthenticatedUser'
import type { GeblokkeerdeWeek } from '../../../planning/types/GeblokkeerdeWeek'
import { PageSection } from '../../../theme/components/PageSection'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import type { Activiteit } from '../types/Activiteit'

import { ActiviteitenDialog } from './ActiviteitenDialog'
import { ActiviteitenTableRow } from './ActiviteitenTableRow'

const filterActiviteiten = (activiteiten, statusFilter, expiredFilter) => {
  let filteredActiviteiten = activiteiten

  if (!statusFilter) {
    filteredActiviteiten = filteredActiviteiten.filter((activiteit) => {
      const isCompleted = activiteit.completed?.date && true

      return !isCompleted
    })
  }

  let filter

  switch (expiredFilter) {
    // If green, show activities that aren't expired yet
    case 'green':
      filter = (activiteit: Activiteit) =>
        futureDay(activiteit.dueDate) && !activiteit.completed
      break
    // Show activities that expire within a week from today
    case 'amber':
      filter = (activiteit: Activiteit) =>
        activiteit.kpi && activiteit.kpi.ragLabel === 'amber'
      break
    // Show only expired activities
    case 'red':
      filter = (activiteit: Activiteit) =>
        pastDay(activiteit.dueDate) && !activiteit.completed
      break
    case 'noduedate':
      filter = (activiteit: Activiteit) => !activiteit.dueDate
      break
    // All others, don't filter on duedate
    default:
      filter = () => true
      break
  }

  filteredActiviteiten = filteredActiviteiten.filter(filter)

  return filteredActiviteiten
}

const sortActiviteiten = (activiteiten) => {
  // Group by has dueDate
  const { noDueDate, hasDueDate } = activiteiten.reduce(
    (acc, activiteit) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      activiteit.dueDate
        ? acc.hasDueDate.push(activiteit)
        : acc.noDueDate.push(activiteit)

      return acc
    },
    { noDueDate: [], hasDueDate: [] },
  )

  // Sort activiteiten with dueDate by dueDate and append activiteiten with no dueDate
  // dueDates in chronological order, dates arriving first at the top, dates arriving last at the bottom
  // and no dueDate activities at the very end
  const sortedByDueDate: any = hasDueDate.sort(
    (a, b) => Number(new Date(a.dueDate)) - Number(new Date(b.dueDate)),
  )

  return [...sortedByDueDate, ...noDueDate]
}

const filterTooltips = {
  red: 'Geef alleen verlopen activiteiten weer',
  amber: 'Geef alle activiteiten die vandaag verlopen weer',
  green: 'Geef alleen nog niet verlopen activiteiten weer',
  grey: 'Geef alle activiteiten weer',
  noduedate: 'Geef alleen de activiteiten zonder opleverdatum weer',
}

const headerCells = [
  { id: 'expired', numeric: false, label: '', fixed: 25 },
  { id: 'type', numeric: false, label: 'Type en status', fixed: 125 },
  { id: 'data.text', numeric: false, label: 'Opmerking' },
  {
    id: 'dueDate',
    numeric: false,
    label: 'Opleverdatum en toegewezen aan',
    fixed: 150,
  },
  { id: 'created', numeric: false, label: 'Aangemaakt (door)', fixed: 150 },
  { id: 'actions', numeric: false, label: 'Acties', fixed: 200 },
]

const cancelledFilter = (x) => ['cancelled', 'done'].includes(x.status)

type Props = {
  activiteiten: Activiteit[]
  entity: Entity
  entityProps?: Partial<Project>
  assignees: GroupedOption[]
  isVerantwoordelijke?: boolean
  geblokkeerdeWeken?: GeblokkeerdeWeek[]
}

export const Activiteiten = ({
  activiteiten,
  entity,
  entityProps,
  assignees,
  isVerantwoordelijke,
  geblokkeerdeWeken = [],
}: Props) => {
  const user = useAuthenticatedUser()
  const { canCreateActiviteit } = user
  const [displayAllStatusses, setDisplayAllStatusses] = useState(false)
  const [displayOnlyExpired, setDisplayOnlyExpired] = useState('')
  const [activiteitenToDisplay, setActiviteitenToDisplay] =
    useState(activiteiten)
  const [type, setType] = useState<
    'create' | 'edit' | 'resolve' | 'cancel' | undefined
  >()
  const [selectedActiviteit, setSelectedActiviteit] = useState<
    Activiteit | undefined
  >()

  useEffect(() => {
    const filtered = filterActiviteiten(
      activiteiten,
      displayAllStatusses,
      displayOnlyExpired,
    )

    setActiviteitenToDisplay(sortActiviteiten(filtered))
  }, [displayAllStatusses, displayOnlyExpired, activiteiten])

  const handleClick = ({
    type,
    activiteit,
  }: {
    type: 'create' | 'edit' | 'resolve' | 'cancel' | undefined
    activiteit?: Activiteit
  }) => {
    setSelectedActiviteit(activiteit)
    setType(type)
  }

  const handleClickFilterExpired = (data) => setDisplayOnlyExpired(data.kpi)

  const isPossibleAssignee = assignees.some((a) => a.label === user.organisatie)

  return (
    <PageSection
      heading="Activiteiten"
      id="activiteiten"
      actions={
        <>
          {canCreateActiviteit && isPossibleAssignee && (
            <>
              <StyledIconButton
                title="Nieuwe activiteit"
                onClick={() => handleClick({ type: 'create' })}
                icon={<GroupAddIcon />}
              />
            </>
          )}

          <ToggleSetButton
            active={displayAllStatusses}
            onClick={() => setDisplayAllStatusses(!displayAllStatusses)}
            toggledSetSize={activiteiten.filter(cancelledFilter).length}
            type="activiteiten"
          />

          <FilterKpi
            onFilterChange={handleClickFilterExpired}
            iconClass={displayOnlyExpired}
            isActiviteiten="true"
            tooltips={filterTooltips}
            {...({} as any)}
          />
        </>
      }
    >
      <Box>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              Open {displayAllStatusses && 'en afgehandelde'} activiteiten (
              {activiteitenToDisplay.length})
            </Typography>
          </AccordionSummary>

          <StyledTable
            variant="details"
            headerCells={headerCells}
            {...({} as any)}
          >
            {activiteitenToDisplay.map((activiteit) => {
              return (
                <ActiviteitenTableRow
                  key={activiteit._id}
                  activiteit={activiteit}
                  user={user}
                  handleClickFinish={() =>
                    handleClick({
                      type: 'resolve',
                      activiteit,
                    })
                  }
                  handleClickEdit={() =>
                    handleClick({
                      type: 'edit',
                      activiteit,
                    })
                  }
                  handleClickCancel={() =>
                    handleClick({
                      type: 'cancel',
                      activiteit,
                    })
                  }
                  isVerantwoordelijke={Boolean(isVerantwoordelijke)}
                />
              )
            })}
          </StyledTable>
        </Accordion>
      </Box>

      <ActiviteitenDialog
        type={type}
        entity={entity}
        activiteit={selectedActiviteit}
        entityProps={entityProps}
        onClose={() => setType(undefined)}
        assignees={assignees}
        geblokkeerdeWeken={geblokkeerdeWeken}
      />
    </PageSection>
  )
}
